import { QueryClient, QueryFunction, MutationCache, QueryKey } from "@tanstack/react-query";
import type { PaginatedResponse } from "./use-paginated-query";

export function getCsrfToken(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.split(";").map(s => s.trim()).find(s => s.startsWith("csrf_token="));
  return match ? decodeURIComponent(match.slice("csrf_token=".length)) : undefined;
}

class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthenticationError";
  }
}

export function isHtmlBody(text: string): boolean {
  if (!text) return false;
  const head = text.trimStart().slice(0, 200).toLowerCase();
  return head.startsWith("<!doctype") || head.startsWith("<html") || head.startsWith("<!--");
}

export function isJsonContentType(contentType: string | null | undefined): boolean {
  if (!contentType) return false;
  const ct = contentType.toLowerCase();
  return ct.includes("application/json") || ct.includes("+json");
}

export function extractErrorMessage(text: string, status: number, contentType?: string | null): string {
  if (isJsonContentType(contentType) || (text && (text.trimStart().startsWith("{") || text.trimStart().startsWith("[")))) {
    try {
      const json = JSON.parse(text);
      if (json.userMessage) return json.userMessage;
      if (json.message) return json.message;
    } catch {} /* JSON.parse fallback — non-JSON response handled below */
  }

  if (isHtmlBody(text)) {
    if (status >= 500) return "Server error. Please try again.";
    return "Couldn't load data. Please try again.";
  }

  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You don't have permission to perform this action.";
  if (status === 429) return "Too many requests. Please try again later.";
  if (text && text.length > 0) return text;
  if (status >= 500) return "An unexpected error occurred. Please try again.";
  return "Request failed";
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const contentType = res.headers.get("content-type");
    const text = (await res.text()) || res.statusText;
    const message = extractErrorMessage(text, res.status, contentType);
    if (res.status === 401) {
      throw new AuthenticationError(message);
    }
    const error = new Error(message);
    (error as any).status = res.status;
    let json: any = null;
    if (isJsonContentType(contentType) || text.trimStart().startsWith("{") || text.trimStart().startsWith("[")) {
      try { json = JSON.parse(text); } catch {} /* non-JSON body — ignore */
    }
    if (json && typeof json === "object") {
      (error as any).body = json;
      if (json.errorCode) (error as any).errorCode = json.errorCode;
      if (json.missingTrigger !== undefined) (error as any).missingTrigger = json.missingTrigger;
      if (json.missingAction !== undefined) (error as any).missingAction = json.missingAction;
      if (json.errorType) (error as any).errorType = json.errorType;
      if (json.code) (error as any).code = json.code;
      if (json.stale) (error as any).stale = json.stale;
      if (json.updatedInvoices) (error as any).updatedInvoices = json.updatedInvoices;
      if (json.validationErrors) (error as any).validationErrors = json.validationErrors;
      if (json.validationWarnings) (error as any).validationWarnings = json.validationWarnings;
      if (json.warnings) (error as any).validationWarnings = json.warnings;
      if (json.errors) (error as any).errors = json.errors;
      if (json.feature) (error as any).feature = json.feature;
    }
    throw error;
  }
}

/**
 * Parse a successful response as JSON, but throw a friendly error if the body
 * isn't actually JSON (e.g., the SPA catch-all returned HTML during a server
 * restart). Prevents raw `Unexpected token '<'` errors from leaking to users.
 */
export async function safeJson<T = unknown>(res: Response): Promise<T> {
  const contentType = res.headers.get("content-type");
  if (isJsonContentType(contentType)) {
    return (await res.json()) as T;
  }
  const text = await res.text();
  if (!text) {
    return undefined as unknown as T;
  }
  if (isHtmlBody(text)) {
    const err = new Error("Couldn't load data. Please try again.");
    (err as any).status = res.status;
    (err as any).nonJsonBody = true;
    throw err;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    const err = new Error("Couldn't load data. Please try again.");
    (err as any).status = res.status;
    (err as any).nonJsonBody = true;
    throw err;
  }
}

export { AuthenticationError };

export function getHttpErrorStatus(error: unknown): number | undefined {
  if (error instanceof Error && "status" in error && typeof (error as Record<string, unknown>).status === "number") {
    return (error as Record<string, unknown>).status as number;
  }
  return undefined;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (data) headers["Content-Type"] = "application/json";
  const csrf = getCsrfToken();
  if (csrf) headers["x-csrf-token"] = csrf;

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  (async ({ queryKey }: { queryKey: any }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await safeJson(res);
  }) as any;

const mutationCache = new MutationCache({
  onError: (error: Error) => {
    const status = (error as any).status;
    if (status === 401) {
      window.location.href = "/";
    }
  },
});

export const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
      onError: (error: Error) => {
        if (error instanceof AuthenticationError) {
          queryClient.setQueryData(["/api/auth/me"], null);
          queryClient.clear();
          window.location.href = "/?auth_error=" + encodeURIComponent("Your session has expired. Please sign in again.");
        }
      },
    },
  },
});

export type QuerySnapshot<T = unknown> = [QueryKey, T | undefined][];

export async function optimisticListUpdate<T extends { id: string }>(
  baseQueryKey: string,
  ids: string[],
  updater: (item: T) => T,
): Promise<QuerySnapshot<PaginatedResponse<T>>> {
  await queryClient.cancelQueries({ queryKey: [baseQueryKey] });
  const previousQueries = queryClient.getQueriesData<PaginatedResponse<T>>({ queryKey: [baseQueryKey] });
  const idSet = new Set(ids);
  queryClient.setQueriesData<PaginatedResponse<T>>(
    { queryKey: [baseQueryKey] },
    (old) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((item) => (idSet.has(item.id) ? updater(item) : item)),
      };
    },
  );
  return previousQueries;
}

export function rollbackFromSnapshot<T = unknown>(snapshot: QuerySnapshot<T> | undefined): void {
  if (!snapshot) return;
  for (const [key, data] of snapshot) {
    queryClient.setQueryData(key, data);
  }
}
